import ContentLoader from '@rio-cloud/rio-uikit/ContentLoader';
import Map from '@rio-cloud/rio-uikit/Map';
import Marker from '@rio-cloud/rio-uikit/Marker';
import type { BoundingBox, MarkerColor, Position } from '@rio-cloud/rio-uikit/mapTypes';
import Route from '@rio-cloud/rio-uikit/Route';
import SingleMapMarker from '@rio-cloud/rio-uikit/SingleMapMarker';
import {
    type DeliveryStatus,
    type GeoPoint,
    type TransportOrderDetail,
    useGetTransportOrderRouteQuery,
} from 'api/generated/transportOrdersApi';
import NoContentState from 'components/NoContentState';
import { getMapCredentials } from 'config';
import { type ReactNode, useMemo } from 'react';

const mapCredentials = getMapCredentials();

const MAP_HEIGHT = 300;
const MIN_SPAN_DEGREES = 0.08;
const VIEWPORT_PADDING = 0.15;
const MARKER_HEADROOM = 0.2;
const toPosition = ({ latitude, longitude }: GeoPoint): Position => ({ lat: latitude, lng: longitude });
const spread = (values: number[], extraHigh = 0) => {
    const low = Math.min(...values);
    const high = Math.max(...values);
    const span = high - low;
    // A hub-to-hub hop within one city spans almost nothing, and a bare box would frame the curb.
    const padding = Math.max(span * VIEWPORT_PADDING, (MIN_SPAN_DEGREES - span) / 2, 0);

    return { low: low - padding, high: high + padding + span * extraHigh };
};

const routeViewport = (positions: Position[]): BoundingBox | undefined => {
    if (positions.length === 0) {
        return;
    }

    const latitude = spread(
        positions.map(position => position.lat),
        MARKER_HEADROOM
    );
    const longitude = spread(positions.map(position => position.lng));

    return { top: latitude.high, bottom: latitude.low, left: longitude.low, right: longitude.high };
};

const VEHICLE_MARKER_COLOR: Record<DeliveryStatus, MarkerColor> = {
    ON_TIME: 'bg-map-marker-asset',
    DELAYED: 'bg-map-marker-warning',
    STOPPED: 'bg-map-marker-danger',
};

const vehicleMarkerColor = (deliveryStatus?: DeliveryStatus): MarkerColor =>
    deliveryStatus === undefined ? 'bg-map-marker-asset' : VEHICLE_MARKER_COLOR[deliveryStatus];

const MapPlaceholder = ({ height, children }: { height: number; children: ReactNode }) => (
    <div className='display-flex align-items-center justify-content-center bg-lightest' style={{ height }}>
        {children}
    </div>
);

export type TransportOrderDetailMapProps = {
    order: TransportOrderDetail;
    height?: number;
};

const TransportOrderDetailMap = ({ order, height = MAP_HEIGHT }: TransportOrderDetailMapProps) => {
    const { currentData, isFetching, isError } = useGetTransportOrderRouteQuery({
        transportOrderId: order.transportOrderId,
    });

    const positions = useMemo(() => currentData?.waypoints.map(toPosition) ?? [], [currentData]);
    const viewport = useMemo(() => routeViewport(positions), [positions]);

    if (isError) {
        return (
            <MapPlaceholder height={height}>
                <NoContentState iconName='exclamation-sign' headline='Route could not be loaded' />
            </MapPlaceholder>
        );
    }

    if (viewport === undefined) {
        return (
            <MapPlaceholder height={height}>
                {isFetching ? (
                    <ContentLoader width='100%' height={height} className='margin-0' />
                ) : (
                    <NoContentState iconName='map-marker' headline='No route for this order' />
                )}
            </MapPlaceholder>
        );
    }

    const vehicle = order.progressDetails;

    return (
        <div style={{ height }}>
            <Map credentials={mapCredentials} boundingBox={viewport} hideMapSettings>
                <Route
                    key={order.transportOrderId}
                    positions={positions}
                    hasArrows={false}
                    startIcon={<SingleMapMarker iconNames={['start']} markerColor='bg-map-marker-route' />}
                    endIcon={<SingleMapMarker iconNames={['finish']} markerColor='bg-map-marker-route' />}
                />
                {vehicle === undefined ? null : (
                    <Marker
                        position={toPosition(vehicle.currentPosition)}
                        icon={
                            <SingleMapMarker
                                iconNames={['truck']}
                                markerColor={vehicleMarkerColor(order.deliveryStatus)}
                            />
                        }
                    />
                )}
            </Map>
        </div>
    );
};

export default TransportOrderDetailMap;
